import { Spin } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import React, { useEffect, useMemo, useState } from 'react';
import Empty from '@/components/QuietEmpty';
import type { CrmAiBizType } from '@/services/bff/types/ai-biz';
import { ensureValidAccessToken, getAccessToken } from '@/services/oauth2';
import styles from './style.module.less';

type AiChatPanelProps = {
  bizType: CrmAiBizType;
  bizUuid?: string;
  contextTitle?: string;
};

type ChatResourceContext = {
  resourceType: CrmAiBizType;
  resourceId: string;
  title: string;
  allowedModes: ['chat'];
};

type ChatRuntimeProps = {
  baseUrl: string;
  headers: Record<string, string>;
  resourceContext: ChatResourceContext;
  className?: string;
};

type ChatRuntimeComponent = React.ComponentType<ChatRuntimeProps>;

type BizAppConfigWithChat = typeof BizAppConfig & {
  services?: {
    chatPlatform?: {
      baseUrl?: string;
    };
  };
};

declare global {
  interface Window {
    __ZHiyueChatRuntime__?: {
      ChatPanel?: ChatRuntimeComponent;
    };
  }
}

const normalizeUrl = (value?: string) =>
  String(value || '')
    .trim()
    .replace(/\/+$/, '');

const BIZ_TITLE_MAP: Record<CrmAiBizType, string> = {
  lead: '线索详情',
  customer: '客户详情',
  opportunity: '商机详情',
  contact: '联系人详情',
  contract: '合同详情',
  quote: '报价单详情',
};

const readChatBaseUrl = () => {
  const chatConfig = BizAppConfig as BizAppConfigWithChat;
  return normalizeUrl(
    chatConfig.chatPlatformUrl || chatConfig.services?.chatPlatform?.baseUrl,
  );
};

const readTenantUuid = () => {
  try {
    return localStorage.getItem('currentTenantUuid') || '';
  } catch {
    return '';
  }
};

export function AiChatPanel({
  bizType,
  bizUuid,
  contextTitle,
}: AiChatPanelProps) {
  const [headers, setHeaders] = useState<Record<string, string>>();
  const [headersLoading, setHeadersLoading] = useState(false);

  const chatBaseUrl = useMemo(() => readChatBaseUrl(), []);
  const normalizedBizUuid = String(bizUuid || '').trim();
  const normalizedTenantUuid = useMemo(() => readTenantUuid(), []);

  const resourceContext = useMemo<ChatResourceContext | undefined>(() => {
    if (!normalizedBizUuid) {
      return undefined;
    }

    return {
      resourceType: bizType,
      resourceId: normalizedBizUuid,
      title: String(contextTitle || '').trim() || BIZ_TITLE_MAP[bizType],
      allowedModes: ['chat'],
    };
  }, [bizType, contextTitle, normalizedBizUuid]);

  useEffect(() => {
    let cancelled = false;

    if (!chatBaseUrl || !resourceContext) {
      setHeaders(undefined);
      setHeadersLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setHeadersLoading(true);

    void (async () => {
      const token = (await ensureValidAccessToken()) || getAccessToken() || '';
      const nextHeaders: Record<string, string> = {};

      if (token) {
        nextHeaders.Authorization = `Bearer ${token}`;
      }

      if (normalizedTenantUuid) {
        nextHeaders['x-tenant-id'] = normalizedTenantUuid;
      }

      if (!cancelled) {
        setHeaders(nextHeaders);
        setHeadersLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chatBaseUrl, normalizedTenantUuid, resourceContext]);

  if (!chatBaseUrl) {
    return (
      <div className={styles.chatContent} data-crm-ai-content>
        <div className={styles.chatEmpty}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="当前环境未配置 AI 对话服务"
          />
        </div>
      </div>
    );
  }

  if (!resourceContext) {
    return (
      <div className={styles.chatContent} data-crm-ai-content>
        <div className={styles.chatEmpty}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="缺少业务对象，暂时无法加载 AI 对话"
          />
        </div>
      </div>
    );
  }

  const ChatPanel =
    typeof window === 'undefined'
      ? undefined
      : window.__ZHiyueChatRuntime__?.ChatPanel;

  if (!ChatPanel) {
    return (
      <div className={styles.chatContent} data-crm-ai-content>
        <div className={styles.chatEmpty}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="AI 对话组件待主控接入"
          />
        </div>
      </div>
    );
  }

  if (headersLoading || !headers) {
    return (
      <div className={styles.chatContent} data-crm-ai-content>
        <div className={styles.chatLoading}>
          <Spin />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chatContent} data-crm-ai-content>
      <div className={styles.chatPanel} data-crm-ai-chat>
        <ChatPanel
          baseUrl={chatBaseUrl}
          headers={headers}
          resourceContext={resourceContext}
          className={styles.chatRuntime}
        />
      </div>
    </div>
  );
}
